<template>
  <v-row class="text-left full-height">
    <v-col cols="12">
      <DrawerToolbar title="Edit Summary Program" :admin="true" @close="handleCancel" />

      <v-card-text class="pa-4">
        <v-form v-model="form.valid">
          <!-- Program name display -->
          <div class="section-title mb-2">Program</div>
          <p class="body-1 mb-6">{{ programName }}</p>

          <v-divider class="mb-6" />

          <!-- Preview -->
          <div class="section-title mb-4">Preview</div>
          <div class="mb-6">
            <div class="preview-scale-wrapper">
              <div class="preview-scale-content">
                <ProgramSummaryCard
                  :program="previewProgram"
                  :button-color="buttonBackgroundColor"
                  :button-text-color="buttonTextColor"
                  :preview="true"
                  :draft="isDraftProgram"
                />
              </div>
            </div>
          </div>

          <v-divider class="mb-6" />

          <!-- Details -->
          <div class="section-title mb-4">Details</div>
          <v-text-field
            v-model="title"
            label="Title"
            outlined
            dense
            color="brandPrimary"
            hint="Defaults to Program Name from Program Setup"
            persistent-hint
            class="mb-6"
          />

          <!-- Program Image -->
          <div class="section-title mb-4">Program Image</div>
          <DragAndDropImageInput
            :file="imageFile"
            :existing-image-url="existingImageUrlForInput"
            collect-alt-text
            :alt-text="imageAltText"
            :aspect-ratio-value="16 / 9"
            aspect-ratio-text="16 : 9"
            crop-image
            class="mb-3"
            @file-selected="onImageFileSelected"
            @clear-existing-image="handleDeleteImage"
            @update:altText="imageAltText = $event"
          />
          <p class="caption mb-6">
            Image defaulted to the image uploaded from the Program Creator. You may upload a
            different image if you wish. We suggest an image size of 740 x 416px (or any image with
            a 16:9 aspect ratio). JPG, PNG files supported..
          </p>

          <!-- Progress Indicator (learning programs only) -->
          <template v-if="programType === 'learning'">
            <div class="section-title mb-4">Progress Indicator</div>
            <p class="body-2 mb-4">Color Gradient</p>
            <div class="d-flex align-center mb-4">
              <input type="color" v-model="progressPrimaryColor" class="color-swatch mr-2" />
              <v-text-field
                v-model="progressPrimaryColor"
                dense
                outlined
                :rules="hexColorRules"
                label="First color (in hexadecimal)"
                hide-details="auto"
              />
            </div>
            <div class="d-flex align-center mb-6">
              <input type="color" v-model="progressSecondaryColor" class="color-swatch mr-2" />
              <v-text-field
                v-model="progressSecondaryColor"
                dense
                outlined
                :rules="hexColorRules"
                label="Second color (in hexadecimal)"
                hide-details="auto"
              />
            </div>
          </template>

          <!-- Button Styling -->
          <div class="section-title mb-4">Button Styling</div>
          <div class="d-flex align-center mb-4">
            <input type="color" v-model="buttonBackgroundColor" class="color-swatch mr-2" />
            <v-text-field
              v-model="buttonBackgroundColor"
              dense
              outlined
              :rules="hexColorRules"
              label="Button background color (in hexadecimal)"
              hide-details="auto"
            />
          </div>
          <div class="d-flex align-center mb-4">
            <input type="color" v-model="buttonTextColor" class="color-swatch mr-2" />
            <v-text-field
              v-model="buttonTextColor"
              dense
              outlined
              :rules="hexColorRules"
              label="Button text color (in hexadecimal)"
              hide-details="auto"
            />
          </div>
          <p class="caption mb-6">
            Default colors meet WCAG accessibility standards. Ensure any custom colors maintain
            4.5:1 contrast.
          </p>

          <v-divider class="mb-4" />

          <div v-if="error.general" class="mt-2">
            <p class="error--text body-2">{{ error.general }}</p>
          </div>
        </v-form>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="d-flex justify-end pa-4">
        <v-btn text color="brandPrimary" @click="handleCancel">Cancel</v-btn>
        <v-btn
          color="brandPrimary"
          class="white--text"
          rounded
          depressed
          :loading="loading.saving"
          :disabled="!form.valid"
          @click="handleSave"
        >
          Save
        </v-btn>
      </v-card-actions>

      <!-- Cancel confirmation dialog -->
      <UnsavedChangesDialog
        v-model="dialog.confirmCancel"
        @stay="dialog.confirmCancel = false"
        @discard="handleCancelConfirmed"
      />
    </v-col>
  </v-row>
</template>

<script>
import ProgramService from "@/services/ProgramService";
import DragAndDropImageInput from "@/components/DragAndDropImageInput.vue";
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import UnsavedChangesDialog from "@/components/generic/UnsavedChangesDialog.vue";
import ProgramSummaryCard from "@/components/participant/programs/ProgramSummaryCard.vue";
import { isValidImageUrl } from "@/utils/isValidImageUrl";
import { mapState } from "vuex";

function normalizeLegacyTitle(title) {
  if (title && typeof title === "object") {
    return (
      Object.keys(title)
        .filter((k) => !isNaN(k))
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => title[k])
        .join("") || null
    );
  }
  return title || null;
}

function initialState() {
  return {
    title: null,
    imageUrl: null,
    imageFile: null,
    imageFilePreviewUrl: null,
    imageAltText: "",
    imageDeleted: false,
    buttonBackgroundColor: "#077DAF",
    buttonTextColor: "#FFFFFF",
    progressPrimaryColor: "#09ACEC",
    progressSecondaryColor: "#9FCD63",

    // ADR-0001 Part B: snapshots captured at preload time (see
    // `preloadRow()`), used to detect genuine user edits to style/color
    // leaves vs. preloaded/inherited values. A style field's live value is
    // compared against its snapshot in `handleSave()`'s payload builder —
    // still-equal means "never touched this session" (send null so the
    // backend's read-time merge can take over); different means the user
    // explicitly picked a new value (send it concretely). Because the
    // snapshot is captured every time `preloadRow()` runs (initial mount,
    // and any subsequent `row` prop change), a preload — whether from an
    // existing column's config or from globalDefaults on a new/incomplete
    // card — never itself counts as a touch, even when the preloaded value
    // happens to equal the field's hardcoded fallback.
    preloadedButtonBackgroundColor: "#077DAF",
    preloadedButtonTextColor: "#FFFFFF",
    preloadedProgressPrimaryColor: "#09ACEC",
    preloadedProgressSecondaryColor: "#9FCD63",

    hexColorRules: [
      (v) => !v || v.startsWith("#") || "Hex color value should start with #",
      (v) => !v || v.length === 7 || "Hex color value should be 6 characters",
      (v) => !v || /^#[0-9A-Fa-f]{6}$/.test(v) || "Invalid hex color value",
    ],

    form: { valid: true },
    dialog: { confirmCancel: false },
    loading: { saving: false },
    error: { general: null },
  };
}

// ADR-0001 Part B: deep, per-field null-coalesce merge used when persisting
// an edit to an existing column. `handleSave()`'s payload builder
// intentionally nulls out untouched style leaves so the backend's read-time
// merge can take over -- but the save payload itself still needs to preserve
// any concrete value the row already has for a field the user didn't touch
// *this session*, since this frontend change (alone) does not rewrite
// already-stored rows.
// Null in `newConfig` -> fall back to `existingConfig`'s value for that leaf.
// Non-null in `newConfig` -> always wins (explicit user edit).
// Mirrors ContentCreator.vue's / BillboardCreator.vue's identically-named
// helper (see their doc-comments for the full rationale) -- kept as a local
// copy per-component rather than extracted into a shared module, consistent
// with the rest of this stream's per-component touched-tracking
// implementations.
function nullCoalesceMergeConfig(existingConfig, newConfig) {
  const existing = existingConfig || {};
  const merged = { ...existing };
  for (const [key, value] of Object.entries(newConfig || {})) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const existingNested =
        existing[key] && typeof existing[key] === "object" ? existing[key] : {};
      const mergedNested = { ...existingNested };
      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        mergedNested[nestedKey] =
          nestedValue != null ? nestedValue : existingNested[nestedKey] ?? null;
      }
      merged[key] = mergedNested;
    } else {
      merged[key] = value != null ? value : existing[key] ?? null;
    }
  }
  return merged;
}

export default {
  name: "SummaryCreator",
  props: {
    row: {
      type: Object,
      required: true,
    },
    globalDefaults: { type: Object, default: () => ({}) },
  },
  components: { DragAndDropImageInput, DrawerToolbar, UnsavedChangesDialog, ProgramSummaryCard },
  data() {
    return initialState();
  },
  created() {
    this.preloadRow();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    resetPage() {
      if (this.imageFilePreviewUrl) {
        URL.revokeObjectURL(this.imageFilePreviewUrl);
      }
      Object.assign(this.$data, initialState());
    },
    preloadRow() {
      this.resetPage();
      if (!this.row) return;

      const column = this.row?.DashboardColumns?.[0];
      const config = column?.config || {};

      this.title = normalizeLegacyTitle(config?.title);
      this.imageUrl = config?.image?.url || this.row?.Program?.imageURL || null;
      this.imageAltText = config?.image?.altText || "";
      // ADR-0001 Part B: the *displayed* value always resolves through the
      // same null-coalesce chain the backend now uses at read time — an
      // explicit config value wins, otherwise fall back to the client's
      // globalDefaults, otherwise the hardcoded fallback. This applies
      // whether the card is brand-new (isNew) or already exists with that
      // particular style field left unset — mirroring the backend's
      // read-time merge instead of only special-casing brand-new cards.
      //
      // `preloaded<Field>` snapshots are captured in the same statement so
      // that `handleSave()` can later distinguish "still shows the
      // preloaded/inherited value" (send null — not touched this session)
      // from "the user picked a different value" (send it concretely).
      this.buttonBackgroundColor =
        config?.buttonBackgroundColor || this.globalDefaults?.buttonBackgroundColor || "#077DAF";
      this.preloadedButtonBackgroundColor = this.buttonBackgroundColor;

      this.buttonTextColor =
        config?.buttonTextColor || this.globalDefaults?.buttonTextColor || "#FFFFFF";
      this.preloadedButtonTextColor = this.buttonTextColor;

      if (this.programType === "learning") {
        this.progressPrimaryColor = config?.progressPrimaryColor || "#09ACEC";
        this.progressSecondaryColor = config?.progressSecondaryColor || "#9FCD63";
      }
      this.preloadedProgressPrimaryColor = this.progressPrimaryColor;
      this.preloadedProgressSecondaryColor = this.progressSecondaryColor;
    },

    handleDeleteImage() {
      this.imageUrl = null;
      this.imageFile = null;
      this.imageDeleted = true;
      if (this.imageFilePreviewUrl) {
        URL.revokeObjectURL(this.imageFilePreviewUrl);
        this.imageFilePreviewUrl = null;
      }
    },

    onImageFileSelected(file) {
      if (this.imageFilePreviewUrl) {
        URL.revokeObjectURL(this.imageFilePreviewUrl);
        this.imageFilePreviewUrl = null;
      }
      this.imageFile = file;
      if (file) {
        this.imageFilePreviewUrl = URL.createObjectURL(file);
        this.imageDeleted = false;
      }
    },

    handleCancel() {
      if (this.isDirty) {
        this.dialog.confirmCancel = true;
      } else {
        this.close();
      }
    },

    handleCancelConfirmed() {
      this.dialog.confirmCancel = false;
      this.close();
    },

    async handleSave() {
      if (!this.form.valid || this.loading.saving) return;

      this.loading.saving = true;
      this.error.general = null;

      try {
        const effectiveImageUrl = this.imageUrl || this.row?.Program?.imageURL || null;

        // ADR-0001 Part B: only send a concrete value for a style/color leaf
        // that differs from its preload-time snapshot (i.e. the user
        // actually changed it this session); fields still equal to what was
        // preloaded/inherited are sent as null so the backend's read-time
        // null-coalesce merge can fall back to ClientDashboardDefaults.
        const touchedTrackedConfig = {
          buttonBackgroundColor:
            this.buttonBackgroundColor !== this.preloadedButtonBackgroundColor
              ? this.buttonBackgroundColor
              : null,
          buttonTextColor:
            this.buttonTextColor !== this.preloadedButtonTextColor ? this.buttonTextColor : null,
          ...(this.programType === "learning"
            ? {
                progressPrimaryColor:
                  this.progressPrimaryColor !== this.preloadedProgressPrimaryColor
                    ? this.progressPrimaryColor
                    : null,
                progressSecondaryColor:
                  this.progressSecondaryColor !== this.preloadedProgressSecondaryColor
                    ? this.progressSecondaryColor
                    : null,
              }
            : {}),
        };

        const rowId = this.row?.rowId;
        const columnId =
          this.row?.DashboardColumns?.[0]?.columnId ??
          this.row?.DashboardColumns?.[0]?.dashboardColumnId;

        // Only the touched-tracked style leaves above go through the
        // null-coalesce merge -- `title` and `image` are NOT touched-tracked
        // sentinels, they are always concrete assertions of current UI state
        // (in particular, `image: null` deliberately means "user deleted the
        // image", not "leave untouched"). Merging those against the existing
        // stored config would silently resurrect a just-deleted image, so
        // they must win outright and bypass the merge entirely.
        const existingConfig = this.row?.DashboardColumns?.[0]?.config || {};
        const mergedTouchedTrackedConfig = columnId
          ? nullCoalesceMergeConfig(existingConfig, touchedTrackedConfig)
          : touchedTrackedConfig;

        const payload = {
          ...mergedTouchedTrackedConfig,
          title: this.title,
          image:
            this.imageDeleted && !this.imageFile
              ? null
              : { url: effectiveImageUrl, altText: this.imageAltText },
        };

        if (columnId) {
          if (this.imageFile) {
            const uploadResponse = await ProgramService.uploadDashboardColumnImage(
              columnId,
              this.imageFile
            );
            const imageUrl = uploadResponse?.data?.result;
            if (!isValidImageUrl(imageUrl)) {
              throw new Error("Upload failed");
            }
            payload.image.url = imageUrl;
            this.imageUrl = imageUrl;
          }

          // TODO: #179811 / #181583 — Replace placeholder with actual endpoint
          await ProgramService.updateDashboardColumn(columnId, {
            rowId,
            config: payload,
            clientId: this.clientId,
          });
        } else {
          // TODO: #179811 — No column exists yet for this program row.
          console.warn(
            "TODO: #179811 — No DashboardColumn exists for this program row.",
            "The Default Program Summary Card enabler must be deployed first.",
            { rowId, programId: this.row?.programId }
          );
          this.error.general =
            "Cannot save yet — the program summary card configuration has not been initialized. Please contact your administrator.";
          this.loading.saving = false;
          return;
        }

        this.$emit("refresh");
        this.$emit("close");
      } catch (e) {
        console.error("Error saving program summary", e);
        this.error.general =
          e?.response?.data?.error || e.message || "Failed to save. Please try again.";
      } finally {
        this.loading.saving = false;
      }
    },

    formatProgramDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
  },
  beforeUnmount() {
    if (this.imageFilePreviewUrl) {
      URL.revokeObjectURL(this.imageFilePreviewUrl);
    }
  },
  computed: {
    ...mapState(["userProfile", "clients", "globalClientId"]),
    clientId() {
      return this.globalClientId;
    },
    programName() {
      return this.row?.Program?.displayName || "Unknown Program";
    },
    programType() {
      return this.row?.Program?.type || "standard";
    },
    programStartDate() {
      return this.row?.Program?.startDate;
    },
    programEndDateLabel() {
      const end = this.row?.Program?.endDate;
      if (!end) return "";
      return this.formatProgramDate(end);
    },
    existingImageUrlForInput() {
      if (this.imageDeleted || this.imageFile) return null;
      return this.imageUrl;
    },
    isDraftProgram() {
      return this.row?.Program?.status !== "Active";
    },
    previewImageUrl() {
      if (this.imageDeleted && !this.imageFile) return null;
      return this.imageFilePreviewUrl || this.imageUrl || this.row?.Program?.imageURL || null;
    },
    previewProgram() {
      const base = {
        displayName: this.title || this.programName,
        name: this.programName,
        imageURL: this.previewImageUrl,
        startDate: this.programStartDate,
        endDate: this.row?.Program?.endDate || null,
        type: this.programType,
        programState: "active",
        status: "active",
      };

      if (this.programType === "claim") {
        // Claims: no progress, show both buttons via uuid
        base.uuid = "preview";
      } else {
        // Standard/learning: show progress bar
        base.progressPercent = 50;
        base.progressValue = "10 of 20 modules completed";
      }

      return base;
    },
    isDirty() {
      const column = this.row?.DashboardColumns?.[0];
      const config = column?.config || {};
      const origTitle = normalizeLegacyTitle(config?.title);
      const origImage = config?.image?.url || this.row?.Program?.imageURL || null;
      const origAltText = config?.image?.altText || "";
      const origBtnBg = config?.buttonBackgroundColor || "#077DAF";
      const origBtnText = config?.buttonTextColor || "#FFFFFF";

      return (
        (this.title || null) !== origTitle ||
        this.imageUrl !== origImage ||
        this.imageAltText !== origAltText ||
        this.imageDeleted ||
        this.imageFile !== null ||
        this.buttonBackgroundColor !== origBtnBg ||
        this.buttonTextColor !== origBtnText ||
        (this.programType === "learning" &&
          (this.progressPrimaryColor !== (config?.progressPrimaryColor || "#09ACEC") ||
            this.progressSecondaryColor !== (config?.progressSecondaryColor || "#9FCD63")))
      );
    },
  },
  watch: {
    row: {
      handler() {
        this.preloadRow();
      },
    },
  },
};
</script>

<style scoped>
.section-title {
  font-size: 16px;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.87);
}

.color-swatch {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0;
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
}

.preview-scale-wrapper {
  width: 100%;
  overflow: hidden;
  /* Height = natural card height × scale factor. The card is roughly 200px tall at 600px wide,
     so at 0.55 scale the wrapper needs to be about 110px. Using aspect-ratio is more robust: */
  aspect-ratio: 3 / 1;
  position: relative;
}

.preview-scale-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 181.8%; /* 1 / 0.55 = ~1.818, so content renders at full size then scales down */
  transform: scale(0.55);
  transform-origin: top left;
}
</style>
